import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../services/api';
import { Building2, Users, LogOut, Settings } from 'lucide-react';

export default function AdminDashboard() {
  const navigate = useNavigate();

  const { data: adminData, isLoading: adminLoading, error: adminError } = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: async () => {
      const response = await adminApi.me();
      return response.data;
    },
  });

  const { data: tenantsData, isLoading: tenantsLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const response = await adminApi.getTenants();
      return response.data;
    },
  });

  useEffect(() => {
    if (adminError) {
      navigate('/admin/login');
    }
  }, [adminError, navigate]);

  const handleLogout = async () => {
    try {
      const response = await adminApi.logout();
      // Also end the Microsoft session, so the next sign-in can pick another account
      window.location.href = response.data.signOutUrl;
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/admin/login');
    }
  };

  if (adminLoading || tenantsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const activeTenants = tenantsData?.tenants?.filter((t: any) => t.is_active) || [];
  const inactiveTenants = tenantsData?.tenants?.filter((t: any) => !t.is_active) || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Admin dashboard</h1>
            <p className="text-sm text-gray-600">Welcome, {adminData?.admin?.name || adminData?.admin?.username}
              {adminData?.admin && !adminData.admin.isAdmin && ' (read-only)'}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active tenants</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{activeTenants.length}</p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <Building2 className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Inactive tenants</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{inactiveTenants.length}</p>
              </div>
              <div className="bg-red-100 rounded-full p-3">
                <Building2 className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Tenants</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{tenantsData?.count || 0}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card mb-8">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-[15px] font-semibold text-ink">Quick actions</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/admin/tenants/new"
              className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
            >
              <Building2 className="w-6 h-6 text-blue-600" />
              <span className="font-medium text-gray-900">Create New Tenant</span>
            </Link>

            <Link
              to="/admin/tenants"
              className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
            >
              <Settings className="w-6 h-6 text-blue-600" />
              <span className="font-medium text-gray-900">Manage tenants</span>
            </Link>
          </div>
        </div>

        {/* Recent Tenants */}
        <div className="card">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-[15px] font-semibold text-ink">Recent tenants</h2>
            <Link to="/admin/tenants" className="text-sm font-medium text-blue-600 hover:text-blue-700">
              View all
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subdomain
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tenantsData?.tenants?.slice(0, 5).map((tenant: any) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {tenant.company_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {tenant.subdomain}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          tenant.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {tenant.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
